// Notice API service — Laravel apiResource: /api/notices

import { apiRequest } from './api';

// ── API record types ────────────────────────────────────────

export type NoticeAudience = {
    id: string;
    notice_id: string;
    class_id: string;
    section_id: string | null;
    school_class?: { id: string; name: string } | null;
    section?: { id: string; name: string } | null;
};

export type NoticeApiRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    title: string;
    content: string | null;
    type: 'general' | 'academic' | 'event' | 'emergency' | 'fee_update';
    priority: 'urgent' | 'high' | 'normal' | 'low';
    is_school_wide: boolean;
    requires_acknowledgment: boolean;
    published_at: string | null;
    expires_at: string | null;
    created_at: string;
    updated_at: string;
    audiences?: NoticeAudience[];
    acknowledgments_count?: number;
    total_audience_count?: number;
};

export type NoticePayload = {
    school_id: string;
    title: string;
    content: string;
    type: string;
    priority: string;
    is_school_wide: boolean;
    requires_acknowledgment: boolean;
    published_at: string | null;
    expires_at: string | null;
    audiences?: { class_id: string; section_id: string | null }[];
};

export type NoticeAcknowledgment = {
    id: string;
    notice_id: string;
    user_id: string;
    acknowledged_at: string | null;
    user?: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string | null;
        role?: string;
        avatar_url?: string | null;
    };
};

export type NoticeListResponse = {
    success: boolean;
    message: string;
    data: NoticeApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

export type AcknowledgmentListResponse = {
    success: boolean;
    message: string;
    data: NoticeAcknowledgment[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

// ── API Functions ───────────────────────────────────────────

/**
 * List notices with optional pagination.
 */
export async function fetchNotices(params?: {
    page?: number;
    perPage?: number;
}): Promise<NoticeListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<NoticeApiRecord[]>(`/api/notices${qs ? `?${qs}` : ''}`);

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as NoticeListResponse).meta,
    };
}

/**
 * Get a single notice by ID.
 */
export async function fetchNotice(id: string): Promise<NoticeApiRecord> {
    const res = await apiRequest<NoticeApiRecord>(`/api/notices/${id}`);
    return res.data;
}

/**
 * Create a new notice.
 */
export async function createNotice(payload: NoticePayload): Promise<NoticeApiRecord> {
    const res = await apiRequest<NoticeApiRecord>('/api/notices', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Update an existing notice.
 */
export async function updateNotice(id: string, payload: NoticePayload): Promise<NoticeApiRecord> {
    const res = await apiRequest<NoticeApiRecord>(`/api/notices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Delete a notice.
 */
export async function deleteNotice(id: string): Promise<void> {
    await apiRequest(`/api/notices/${id}`, { method: 'DELETE' });
}

/**
 * Fetch acknowledgment receipts for a notice.
 */
export async function fetchAcknowledgments(noticeId: string, params?: {
    page?: number;
    perPage?: number;
}): Promise<AcknowledgmentListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<NoticeAcknowledgment[]>(
        `/api/notices/${noticeId}/acknowledgments${qs ? `?${qs}` : ''}`
    );

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as AcknowledgmentListResponse).meta,
    };
}

/**
 * Send a reminder to users who haven't acknowledged a notice.
 */
export async function sendReminder(noticeId: string): Promise<{ message: string }> {
    const res = await apiRequest<{ message: string }>(
        `/api/notices/${noticeId}/remind`,
        { method: 'POST' }
    );
    return res.data;
}
